/**
 * Computed-tag fixtures — seed the demo tenant with three representative
 * examples so the page isn't empty on first load.
 *
 * Each one demonstrates a different pattern:
 *   - Linear compensation (Pt100-style — but applied to a thermocouple
 *     for the demo so the live preview shows a believable value)
 *   - Ratio (yield from accepted / total castings)
 *   - Composite KPI (carbon equivalent — straight from the foundry
 *     chemistry namespace)
 */

import type { ComputedTagDef } from "./types";

const NOW = "2026-05-24T10:00:00.000Z";

export const FX_COMPUTED_TAGS: ComputedTagDef[] = [
  {
    id: "ct-bath-temp-compensated",
    name: "Compensated bath temperature",
    description:
      "Linear cold-junction compensation on the furnace bath thermocouple. Removes ~2.4% of the temperature delta between the bath and the ambient.",
    outputUnit: "°C",
    outputDataKind: "analog_scalar",
    inputs: [
      { tagId: "tag-furn1-bath-temp", alias: "T_raw" },
      { tagId: "tag-furn1-ambient", alias: "T_amb" },
    ],
    expression: "T_raw - 0.024 * (T_raw - T_amb)",
    trigger: { kind: "on_input_change", debounceMs: 250 },
    enabled: true,
    createdAt: NOW,
    updatedAt: NOW,
  },
  {
    id: "ct-casting-yield",
    name: "Casting yield",
    description: "Pass-rate ratio of accepted to total castings per heat.",
    outputUnit: "%",
    outputDataKind: "analog_percentage",
    inputs: [
      { tagId: "tag-castings-accepted", alias: "accepted" },
      { tagId: "tag-castings-total", alias: "total" },
    ],
    expression: "(accepted / total) * 100",
    trigger: { kind: "on_input_change" },
    enabled: true,
    createdAt: NOW,
    updatedAt: NOW,
  },
  {
    id: "ct-carbon-equivalent",
    name: "Carbon equivalent (CE)",
    // GATE-SCOPE3: the third copy of the same sentence. WS-E rewrote
    // presets.ts; the pack YAML the API serves and this stub seed both kept the
    // prediction claim, because the claim gate named two files by hand. The
    // string ships in the customer's bundle whether or not stub mode renders
    // it, so it is held to the same rule.
    description:
      "Foundry chemistry composite computed from measured C, Si and P — the ASTM A247 composition index, read against the 4.3% eutectic point. CE = C + (Si + P) / 3. A computed metric, not a solidification prediction.",
    outputUnit: "%",
    outputDataKind: "derived_kpi",
    inputs: [
      { tagId: "tag-chem-carbon", alias: "C" },
      { tagId: "tag-chem-silicon", alias: "Si" },
      { tagId: "tag-chem-phosphorus", alias: "P" },
    ],
    expression: "C + (Si + P) / 3",
    trigger: { kind: "on_input_change" },
    enabled: true,
    createdAt: NOW,
    updatedAt: NOW,
    presetId: "carbon-equivalent",
  },
];

/**
 * Synthetic seed values for the fixture inputs. The live-preview hook
 * uses these as the starting point + a 1 s random walk to keep the
 * preview feeling alive without coupling to the broader simulation
 * engine.
 */
export const FX_INPUT_SEED_VALUES: Record<string, { value: number; unit?: string; drift: number }> = {
  "tag-furn1-bath-temp": { value: 1438, unit: "°C", drift: 4 },
  "tag-furn1-ambient": { value: 38, unit: "°C", drift: 0.3 },
  "tag-castings-accepted": { value: 84, unit: "count", drift: 0 },
  "tag-castings-total": { value: 88, unit: "count", drift: 0 },
  "tag-chem-carbon": { value: 3.12, unit: "%", drift: 0.01 },
  "tag-chem-silicon": { value: 2.05, unit: "%", drift: 0.01 },
  "tag-chem-phosphorus": { value: 0.04, unit: "%", drift: 0.002 },
  "tag-furn1-power-kw": { value: 415, unit: "kW", drift: 8 },
  "tag-sand-moisture": { value: 3.92, unit: "%", drift: 0.04 },
  "tag-pour-temperature": { value: 1388, unit: "°C", drift: 3 },
  "tag-furn1-tilt-angle": { value: 5, unit: "deg", drift: 0.5 },
  "tag-rtd-resistance": { value: 138.5, unit: "Ω", drift: 0.05 },
  "tag-availability": { value: 86.4, unit: "%", drift: 0.5 },
  "tag-performance": { value: 79.1, unit: "%", drift: 0.6 },
  "tag-quality": { value: 96.1, unit: "%", drift: 0.2 },
  "tag-furn1-kwh-counter": { value: 12450, unit: "kWh", drift: 0.5 },
  "tag-air-temp": { value: 32, unit: "°C", drift: 0.3 },
  "tag-air-rh": { value: 64, unit: "%", drift: 1.0 },
};

/**
 * Tag catalog for the editor's input picker. Phase 0 = static. Phase 1
 * fetches from `/v1/iot/tags`. Each entry mirrors the shape of the
 * `tags.json` records in packs/foundry/demo-data so it's swap-compatible.
 */
export interface CatalogTag {
  id: string;
  displayName: string;
  unit?: string;
  source: "sensor" | "computed";
  /**
   * Best-effort kind hint — Phase 0 inferred; Phase 1 comes from the
   * `data_kind` field documented in docs/07 § 4.0.
   */
  dataKind?: import("@foundry/foundry-plant").DataKind;
}

export const FX_CATALOG_TAGS: CatalogTag[] = [
  { id: "tag-furn1-bath-temp", displayName: "Furnace 1 · bath temperature", unit: "°C", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-furn1-ambient", displayName: "Furnace 1 · ambient", unit: "°C", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-furn1-power-kw", displayName: "Furnace 1 · power", unit: "kW", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-furn1-tilt-angle", displayName: "Furnace 1 · tilt angle", unit: "deg", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-furn1-kwh-counter", displayName: "Furnace 1 · kWh counter", unit: "kWh", source: "sensor", dataKind: "count_scalar" },
  { id: "tag-sand-moisture", displayName: "Sand Mixer A · moisture", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-pour-temperature", displayName: "Pour station · pour temp", unit: "°C", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-chem-carbon", displayName: "Spectrometer · carbon", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-chem-silicon", displayName: "Spectrometer · silicon", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-chem-phosphorus", displayName: "Spectrometer · phosphorus", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-castings-accepted", displayName: "QC · castings accepted", unit: "count", source: "sensor", dataKind: "count_scalar" },
  { id: "tag-castings-total", displayName: "QC · castings total", unit: "count", source: "sensor", dataKind: "count_scalar" },
  { id: "tag-availability", displayName: "Line · availability", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-performance", displayName: "Line · performance", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-quality", displayName: "Line · quality", unit: "%", source: "sensor", dataKind: "analog_percentage" },
  { id: "tag-rtd-resistance", displayName: "Pt100 · raw resistance", unit: "Ω", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-air-temp", displayName: "Ambient air · temperature", unit: "°C", source: "sensor", dataKind: "analog_scalar" },
  { id: "tag-air-rh", displayName: "Ambient air · relative humidity", unit: "%", source: "sensor", dataKind: "analog_percentage" },
];
